import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sheet } from '@/shared/components/Sheet';
import { Skeleton } from '@/shared/components/Skeleton';
import { useToast } from '@/shared/components/Toast';
import { ROUTES } from '@/shared/constants/routes';
import { useCreateWorkspace, useWorkspace } from '@/shared/hooks/useWorkspace';
import { useMyAccount } from '@/shared/hooks/useAccountState';
import { useAuthStore } from '@/shared/stores/auth.store';
import {
  getApiErrorCode,
  getApiErrorDetails,
  getApiErrorMessage,
  detailNumber,
} from '@/shared/lib/api-errors';
import { extraBrandsNeeded, toBillingInterval, type ExtraBrandQuote } from '@/shared/lib/account-pricing';
import { formatMoney } from '@/shared/lib/intl';
import { toPlanState } from '@/shared/services/billing.service';
import { BRAND_COPY_SETTINGS, type BrandCopySetting } from '@/shared/services/workspace.service';
import { ExtraBrandQuotePanel } from '@/shared/components/ExtraBrandConsent';
import { useExtraBrandConsent } from '@/shared/hooks/useExtraBrandConsent';
import { OCCASION_INDUSTRIES } from '@unipostudio/platform-constants';
import { withTaxNote } from '@/shared/lib/tax-note';

interface AddBrandSheetProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Why no brand can be created right now, before anything is sent (ADR-006,
 * ADR-007):
 * - pro / agency: the plan that allows one more brand (the free Starter plan
 *   and grandfathered Classic hold one brand, a second needs Pro);
 * - noQuote: Agency needs an extra brand but the server gave no price it can
 *   charge (no billing currency yet): no price is shown and nothing offered;
 * - paidAgency: the Agency is not a paid subscription (set up by our team,
 *   a timed comp or cancelled), so an extra brand cannot be charged to it.
 */
type Block = 'pro' | 'agency' | 'noQuote' | 'paidAgency';

/** Blocks that a plan change answers; the others need support. */
const PLAN_BLOCKS: ReadonlySet<Block> = new Set(['pro', 'agency']);

const NAME_MIN = 2;
const NAME_MAX = 80;

/** Where a plan error sends people: the Subscription tab of Settings. */
const UPGRADE_PATH = `${ROUTES.SETTINGS}?tab=subscription`;

/**
 * "Add brand" (blueprint M20, F7): a small sheet that creates another brand
 * with a name and an optional industry, then hands over to the brand wizard
 * (S04) scoped to the new brand. Opened from the brand switcher and from the
 * all-brands cards on the overview.
 *
 * The brand goes into the account the signed-in person owns (GET
 * /accounts/me), so that is whose limits are shown before anything is sent:
 * a second brand needs Pro, a fourth needs Agency, and on Agency a brand past
 * the included five shows its monthly price and the new total, then is
 * created with `acceptExtraBrandCharge: true`.
 */
export function AddBrandSheet({ open, onClose }: AddBrandSheetProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { switchWorkspace, workspaces, currentWorkspace } = useWorkspace();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const create = useCreateWorkspace();
  const { data: mine, isLoading: accountLoading } = useMyAccount(open);
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [serverBlock, setServerBlock] = useState<Block | null>(null);
  const [quote, setQuote] = useState<ExtraBrandQuote | null>(null);
  // Copy settings from another brand of this account (M-14).
  const [copyFrom, setCopyFrom] = useState('');
  const [include, setInclude] = useState<BrandCopySetting[]>([...BRAND_COPY_SETTINGS]);

  const allowance = mine?.brands ?? null;
  // The brand goes into the account this person OWNS, whichever brand is in
  // view. A payment for that account is confirmed in the context of one of
  // its own brands, never the (possibly someone else's) brand in view.
  const ownBrandId =
    (mine ? workspaces.find((w) => w.accountId === mine.id)?.id : undefined) ??
    workspaces.find((w) => !!userId && w.ownerId === userId)?.id ??
    null;
  // The account's own brands, which settings can be copied from (M-14).
  const ownBrands = mine ? workspaces.filter((w) => w.accountId === mine.id) : workspaces.filter((w) => !!userId && w.ownerId === userId);
  const viewingOther =
    !!currentWorkspace?.accountId && (mine === null || (!!mine && currentWorkspace.accountId !== mine.id));
  const plan = mine ? toPlanState(allowance?.plan ?? mine.plan) : null;
  const atLimit = !!allowance && allowance.used >= allowance.limit;
  // Only the interval is the client's guess, as a fallback; every price comes
  // from the server's quote in the currency the account can be charged in.
  const interval = toBillingInterval(mine?.subscription?.billingInterval);
  // Extra-brand refusals are read the same way here and in the ownership
  // transfer (shared/components/ExtraBrandConsent).
  const consent = useExtraBrandConsent({
    interval,
    fallbackExtras: (details) =>
      extraBrandsNeeded(
        (detailNumber(details, 'owned') ?? allowance?.used ?? 0) + 1,
        detailNumber(details, 'included') ?? allowance?.included ?? 5,
      ),
    confirmWorkspaceId: ownBrandId,
  });
  /** Razorpay: the first extra brand's subscription is being authorised. */
  const authorising = consent.authorising;

  // An admin's timed comp: nothing to charge an extra brand to.
  const comped = mine?.subscription?.status === 'trialing';
  const knownBlock: Block | null =
    atLimit && (plan === 'starter' || plan === 'classic')
      ? 'pro'
      : atLimit && plan === 'pro'
        ? 'agency'
        : null;
  const block = serverBlock ?? knownBlock;
  const agencyAtLimit = atLimit && plan === 'agency' && !comped;
  const planBlock = block !== null && PLAN_BLOCKS.has(block);

  const reset = (): void => {
    setName('');
    setIndustry('');
    setNameError(null);
    setError(null);
    setServerBlock(null);
    setQuote(null);
    setCopyFrom('');
    setInclude([...BRAND_COPY_SETTINGS]);
  };

  const close = (): void => {
    if (create.isPending || authorising) return;
    reset();
    onClose();
  };

  const send = async (acceptExtraBrandCharge: boolean): Promise<void> => {
    const trimmed = name.trim();
    setError(null);
    try {
      const created = await create.mutateAsync({
        name: trimmed,
        ...(industry === '' ? {} : { industry }),
        ...(acceptExtraBrandCharge ? { acceptExtraBrandCharge: true } : {}),
        ...(copyFrom && include.length > 0 ? { copyFrom, include } : {}),
      });
      switchWorkspace(created);
      toast('success', t('topbar.addBrandSheet.created', { name: created.name }));
      reset();
      onClose();
      void navigate(`${ROUTES.BRAND_SETUP}?from=addbrand`);
    } catch (err) {
      const outcome = await consent.handle(err);
      if (outcome?.kind === 'quote') {
        setQuote(outcome.quote);
        return;
      }
      if (outcome?.kind === 'block') {
        setServerBlock(outcome.block);
        return;
      }
      if (outcome?.kind === 'error') {
        setError(outcome.message);
        return;
      }
      if (outcome?.kind === 'authorised') {
        // The create that follows refreshes billing, the account and the
        // brand list (useCreateWorkspace), so the new limit shows at once.
        await send(true);
        return;
      }
      const code = getApiErrorCode(err);
      const details = getApiErrorDetails(err);
      if (code === 'PLAN_REQUIRED' || code === 'LIMIT_REACHED') {
        setServerBlock(details['requiredPlan'] === 'agency' ? 'agency' : 'pro');
      } else {
        setError(getApiErrorMessage(err, t('topbar.addBrandSheet.failed')));
      }
    }
  };

  /**
   * The plans to change are those of this person's own account: from someone
   * else's brand, switch to one of their own brands first, so Billing shows
   * their account and not the one in view.
   */
  const goToOwnBilling = (): void => {
    const own = viewingOther && ownBrandId ? workspaces.find((w) => w.id === ownBrandId) : undefined;
    if (own) switchWorkspace(own);
    close();
  };

  const submit = async (e?: FormEvent): Promise<void> => {
    e?.preventDefault();
    if (block) return;
    if (name.trim().length < NAME_MIN) {
      setNameError(t('topbar.addBrandSheet.nameTooShort', { min: NAME_MIN }));
      return;
    }
    setNameError(null);
    if (quote) {
      await send(true);
      return;
    }
    // Without consent first: on Agency at its limit the server answers with
    // its quote (EXTRA_BRAND_REQUIRED), shown before the brand exists.
    await send(false);
  };

  const money = (amount: number, cur: string): string => formatMoney(amount, cur, { maximumFractionDigits: 0 });
  const fieldLabel = 'block text-meta font-semibold text-[var(--ink-2)] mb-1';
  const planLabel = plan ? t(`overview.plans.${plan}`) : null;

  const footer = block ? (
    <>
      {planBlock && (
        <Link to={UPGRADE_PATH} className="btn btn-primary no-underline" onClick={goToOwnBilling}>
          {t('topbar.addBrandSheet.upgrade')}
        </Link>
      )}
      <button type="button" className="btn btn-secondary" onClick={close}>
        {planBlock ? t('common.cancel') : t('common.close')}
      </button>
    </>
  ) : (
    <>
      <button type="button" className="btn btn-primary" disabled={create.isPending || authorising || accountLoading} onClick={() => void submit()}>
        {authorising
          ? t('topbar.addBrandSheet.authorising')
          : create.isPending
          ? t('topbar.addBrandSheet.creating')
          : quote
            ? t('topbar.addBrandSheet.confirmExtra', { price: withTaxNote(money(quote.perBrand, quote.currency), quote.currency, t) })
            : t('topbar.addBrandSheet.create')}
      </button>
      <button type="button" className="btn btn-secondary" disabled={create.isPending || authorising} onClick={quote ? () => setQuote(null) : close}>
        {quote ? t('common.back') : t('common.cancel')}
      </button>
    </>
  );

  return (
    <Sheet open={open} onClose={close} id="add-brand-sheet" title={t('topbar.addBrandSheet.title')} width={440} footer={footer}>
      {accountLoading && (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton height={40} rounded="md" />
          <Skeleton height={40} rounded="md" />
        </div>
      )}

      {!accountLoading && block && (
        <div role="status" data-testid="add-brand-block" className="rounded-[12px] border border-[var(--line)] bg-[var(--color-surface-2)] p-4">
          <div className="text-[24px] mb-1" aria-hidden="true">🏷️</div>
          <p className="m-0 mb-1 font-semibold text-body text-[var(--ink)]">{t(`topbar.addBrandSheet.block.${block}.title`)}</p>
          {/* Classic is off sale: it is named only to an account still on it (F36). */}
          <p className="m-0 text-ui text-[var(--ink-2)] leading-relaxed">
            {t(`topbar.addBrandSheet.block.${block}.body`, { plan: t(`overview.plans.${plan === 'classic' ? 'classic' : 'starter'}`) })}
          </p>
        </div>
      )}

      {!accountLoading && !block && quote && (
        <ExtraBrandQuotePanel
          quote={quote}
          title={t('topbar.addBrandSheet.extraTitle', { name: name.trim() })}
          included={allowance?.included ?? 5}
        />
      )}

      {!accountLoading && viewingOther && (
        <p role="note" data-testid="add-brand-own-account" className="m-0 mb-4 rounded-[10px] px-3 py-2 text-ui bg-[var(--color-info-soft)] text-[var(--ink)]">
          {mine ? t('topbar.addBrandSheet.ownAccountNote') : t('topbar.addBrandSheet.newAccountNote')}
        </p>
      )}

      {!accountLoading && !block && !quote && (
        <form id="add-brand-form" className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
          <div>
            <label htmlFor="add-brand-name" className={fieldLabel}>{t('topbar.addBrandSheet.name')}</label>
            <input
              id="add-brand-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('topbar.addBrandSheet.namePlaceholder')}
              aria-invalid={nameError ? true : undefined}
              aria-describedby={nameError ? 'add-brand-name-error' : undefined}
              autoComplete="organization"
              dir="auto"
              maxLength={NAME_MAX}
              autoFocus
            />
            {nameError && (
              <p id="add-brand-name-error" className="text-meta mt-1 m-0" style={{ color: 'var(--color-error)' }}>{nameError}</p>
            )}
          </div>

          <div>
            <label htmlFor="add-brand-industry" className={fieldLabel}>{t('topbar.addBrandSheet.industry')}</label>
            <select id="add-brand-industry" className="input" value={industry} onChange={(e) => setIndustry(e.target.value)}>
              <option value="">{t('topbar.addBrandSheet.industryNone')}</option>
              {OCCASION_INDUSTRIES.map((key) => (
                <option key={key} value={key}>{t(`occasions.settings.industries.${key}`)}</option>
              ))}
            </select>
          </div>

          {ownBrands.length > 0 && (
            <div>
              <label htmlFor="add-brand-copy" className={fieldLabel}>{t('topbar.addBrandSheet.copyFrom')}</label>
              <select id="add-brand-copy" className="input" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
                <option value="">{t('topbar.addBrandSheet.copyNone')}</option>
                {ownBrands.map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
              {copyFrom && (
                <fieldset className="m-0 mt-2 p-0 border-0">
                  <legend className="sr-only">{t('topbar.addBrandSheet.copyWhat')}</legend>
                  <div className="flex flex-wrap gap-x-4">
                    {BRAND_COPY_SETTINGS.map((key) => (
                      <label key={key} className="inline-flex items-center gap-2 min-h-[44px] text-ui text-[var(--ink)] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={include.includes(key)}
                          onChange={() => setInclude((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))}
                        />
                        {t(`topbar.addBrandSheet.copy.${key}`)}
                      </label>
                    ))}
                  </div>
                  <p className="m-0 text-meta text-[var(--ink-3)]">{t('topbar.addBrandSheet.copyHint')}</p>
                </fieldset>
              )}
            </div>
          )}

          {allowance && planLabel && (
            <p className="m-0 text-ui text-[var(--ink-3)]" data-testid="add-brand-allowance">
              {t('topbar.addBrandSheet.allowance', { plan: planLabel, used: allowance.used, limit: allowance.limit })}
              {agencyAtLimit && <> {t('topbar.addBrandSheet.extraNoteNoPrice')}</>}
            </p>
          )}
        </form>
      )}

      {error && (
        <p
          role="alert"
          className="m-0 mt-4 text-ui rounded-[10px] px-3 py-2 border"
          style={{ color: 'var(--color-error)', background: 'var(--color-error-soft)', borderColor: 'var(--color-error)' }}
        >
          {error}
        </p>
      )}
    </Sheet>
  );
}
